/**
 * Pinterest Tag tracking utilities
 *
 * Standard events: https://help.pinterest.com/en/business/article/add-event-codes
 *
 * Mirrors src/utils/facebookPixel.js — every Fb-* helper has a Pin-* twin so
 * call-sites pair them 1:1.
 *
 * Identity matching: every track call sets `external_id` and attaches
 * `click_id` + (for checkout/lead) `order_id` so Pinterest can match
 * conversions back to ad clicks. This is what raises the Event Quality
 * score in Conversions Manager.
 */

import { getExternalId, getPinterestClickId, newOrderId } from './userIdentity';
import { isAnalyticsEnabled } from './isAnalyticsEnabled';

const isPintrkAvailable = () => typeof window !== 'undefined' && window.pintrk;

let identitySet = false;
const ensureIdentity = () => {
    if (!isPintrkAvailable()) return;
    if (identitySet) return;
    const ext = getExternalId();
    if (ext) {
        window.pintrk('set', { external_id: ext });
        identitySet = true;
    }
};

const enrich = (params, includeOrderId) => {
    const out = { ...params };
    const clickId = getPinterestClickId();
    if (clickId) out.click_id = clickId;
    if (includeOrderId) out.order_id = newOrderId();
    return out;
};

const track = (eventName, params = {}, { withOrderId = false } = {}) => {
    if (!isAnalyticsEnabled()) return;
    if (!isPintrkAvailable()) return;
    ensureIdentity();
    window.pintrk('track', eventName, enrich(params, withOrderId));
};

export const trackPinPageView = () => track('pagevisit');

export const trackPinAddToCart = (productName, sourceLabel) =>
    track(
        'addtocart',
        {
            product_name: `${productName} - ${sourceLabel}`,
            product_category: productName,
            value: 0.5,
            currency: 'USD',
        },
        { withOrderId: true }
    );

export const trackPinDownload = (mapType) =>
    track(
        'lead',
        { lead_type: `Download - ${mapType}` },
        { withOrderId: true }
    );

export const trackPinImageUpload = () => track('custom', { event_name: 'upload_photo' });

export const trackPinStatsConfigChange = (mapType, position) =>
    track('custom', { event_name: 'stats_config_change', map_type: mapType, position });

export const trackPinShare = (mapType, network) =>
    track('custom', { event_name: 'share', map_type: mapType, network });

